import { useMemo, useState } from 'react';
import { FilterBar } from '@/components/common/FilterBar';
import { DateRangePicker, type RangeValue } from '@/components/forms/DateRangePicker';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { swapFreeControlApi } from '@/api/services/swapFreeControl';
import type { GetHistoryDealsInput, HistoryDealDto } from '@/api/types/swapFreeControl';
import { fmtAddedSwaps, fmtDate } from '../helpers';

export function HistoryTab() {
  // No default range (empty = all) — match legacy.
  const [range, setRange] = useState<RangeValue | null>(null);

  const extra = useMemo<Partial<GetHistoryDealsInput>>(
    () => ({
      startDate: range?.start || undefined,
      endDate: range?.end || undefined,
      sorting: 'date desc',
    }),
    [range],
  );
  const table = useServerDataTable<HistoryDealDto, Partial<GetHistoryDealsInput>>({
    queryKey: ['swap-free', 'history'],
    fetcher: (req) => swapFreeControlApi.getHistoryDeals(req as GetHistoryDealsInput),
    pageSize: 5,
    extraParams: extra,
  });

  const columns: DataGridColumn<HistoryDealDto>[] = [
    { key: 'date', title: 'Date', className: 'text-start', filterable: false, value: (r) => r.date, cell: (r) => fmtDate(r.date) },
    { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login },
    { key: 'positionId', title: 'Ticket', className: 'text-start', value: (r) => r.positionId },
    { key: 'symbol', title: 'Symbol', className: 'text-start', value: (r) => r.symbol ?? '' },
    {
      key: 'addedSwaps',
      title: 'Added Swaps',
      className: 'text-end',
      filterable: false,
      value: (r) => (r.addedSwaps == null || Number.isNaN(r.addedSwaps) ? '' : r.addedSwaps),
      cell: (r) => fmtAddedSwaps(r.addedSwaps),
    },
    { key: 'currency', title: 'Currency', className: 'text-start', value: (r) => r.currency ?? '' },
  ];

  return (
    <div>
      <FilterBar
        card={false}
        className="mb-3"
        fields={[
          {
            node: (
              <div key="date">
                <label className="form-label">Date Range</label>
                <DateRangePicker
                  mode="range"
                  value={range}
                  onChange={(v) => {
                    table.setPageIndex(0);
                    setRange(v);
                  }}
                />
              </div>
            ),
            pinned: true,
            width: 270,
          },
        ]}
      />

      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => `${r.login}-${r.positionId}-${r.date}`}
        card
        includeExcel
        excelFileName="Swap Charge History"
        columnVisibilityToggle={false}
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />
    </div>
  );
}
